import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { 
  Search, 
  Loader2, 
  AlertTriangle, 
  TrendingDown, 
  TrendingUp, 
  Minus,
  ChevronRight,
  Users,
  Target,
  Award,
  AlertCircle
} from 'lucide-react';

const SlowAverageFastLearnersWidget = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('All');
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedTest, setSelectedTest] = useState('iaTest1');
  const [expandedStudent, setExpandedStudent] = useState(null);

  // Subject mapping
  const SUBJECT_MAP = {
    'aml': { name: 'Applied Mathematics-I', code: 'AM-I', max: 20 },
    'ap': { name: 'Applied Physics', code: 'AP', max: 15 },
    'ac': { name: 'Applied Chemistry', code: 'AC', max: 15 },
    'em': { name: 'Engineering Mechanics', code: 'EM', max: 20 },
    'bee': { name: 'Basic Electrical & Electronics', code: 'BEE', max: 20 },
    'pce': { name: 'Professional Communication & Ethics', code: 'PCE', max: 15 }
  };

  useEffect(() => {
    fetchData();
  }, [selectedTest]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const iaRef = collection(db, selectedTest);
      const q = query(iaRef, orderBy('percentage', 'asc'));
      const snapshot = await getDocs(q);

      const docs = snapshot.docs.map((d) => {
        const raw = d.data();
        const percentage = raw.percentage !== undefined && raw.percentage !== null
          ? Number(raw.percentage)
          : NaN;
        
        return {
          id: d.id,
          rollNumber: raw.rollNumber || '',
          studentName: raw.studentName || '',
          subjects: raw.subjects || {},
          total: raw.total || 0,
          percentage,
          division: raw.division || 'Unknown',
          batch: raw.batch || '',
        };
      });

      setData(docs);
    } catch (err) {
      console.error('Error fetching test data:', err);
      setError('Failed to load test data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Enhanced categorization with insights
  const categorized = data.reduce(
    (acc, s) => {
      const p = s.percentage;
      if (!Number.isFinite(p)) {
        acc.noData.push(s);
      } else if (p < 40) {
        acc.needsSupport.push(s);
      } else if (p >= 40 && p < 60) {
        acc.developing.push(s);
      } else if (p >= 60 && p < 75) {
        acc.proficient.push(s);
      } else {
        acc.advanced.push(s);
      }
      return acc;
    },
    { needsSupport: [], developing: [], proficient: [], advanced: [], noData: [] }
  );

  // Calculate insights
  const totalStudents = data.length;
  const avgPercentage = totalStudents > 0 
    ? (data.reduce((sum, s) => sum + (Number.isFinite(s.percentage) ? s.percentage : 0), 0) / totalStudents).toFixed(2)
    : 0;

  // Get divisions
  const divisions = Array.from(
    new Set(data.map((d) => d.division || 'Unknown'))
  ).sort();

  // Filter active list
  const getActiveList = () => {
    let list = [];
    
    switch (activeCategory) {
      case 'needsSupport':
        list = categorized.needsSupport;
        break;
      case 'developing':
        list = categorized.developing;
        break;
      case 'proficient':
        list = categorized.proficient;
        break;
      case 'advanced':
        list = categorized.advanced;
        break;
      case 'noData':
        list = categorized.noData;
        break;
      default:
        list = [...data];
    }

    if (divisionFilter && divisionFilter !== 'All') {
      list = list.filter((s) => s.division === divisionFilter);
    }

    const q = searchQuery.trim().toLowerCase();
    if (q.length > 0) {
      list = list.filter((s) => {
        return (
          String(s.rollNumber).toLowerCase().includes(q) ||
          String(s.studentName).toLowerCase().includes(q)
        );
      });
    }

    return list.sort((a, b) => {
      const pa = Number.isFinite(a.percentage) ? a.percentage : -1;
      const pb = Number.isFinite(b.percentage) ? b.percentage : -1;
      return pa - pb;
    });
  };

  const activeList = getActiveList();

  const getCategoryConfig = (category) => {
    const configs = {
      needsSupport: {
        label: 'Needs Support',
        icon: AlertCircle,
        color: 'red',
        bgGradient: 'from-red-50 to-red-100',
        borderColor: 'border-red-200',
        textColor: 'text-red-700',
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
        ringColor: 'ring-red-300',
        description: '< 40%',
        recommendation: 'Immediate intervention required'
      },
      developing: {
        label: 'Developing',
        icon: TrendingUp,
        color: 'orange',
        bgGradient: 'from-orange-50 to-orange-100',
        borderColor: 'border-orange-200',
        textColor: 'text-orange-700',
        iconBg: 'bg-orange-100',
        iconColor: 'text-orange-600',
        ringColor: 'ring-orange-300',
        description: '40-59%',
        recommendation: 'Regular monitoring needed'
      },
      proficient: {
        label: 'Proficient',
        icon: Target,
        color: 'blue',
        bgGradient: 'from-blue-50 to-blue-100',
        borderColor: 'border-blue-200',
        textColor: 'text-blue-700',
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        ringColor: 'ring-blue-300',
        description: '60-74%',
        recommendation: 'On track, encourage growth'
      },
      advanced: {
        label: 'Advanced',
        icon: Award,
        color: 'green',
        bgGradient: 'from-green-50 to-green-100',
        borderColor: 'border-green-200',
        textColor: 'text-green-700',
        iconBg: 'bg-green-100',
        iconColor: 'text-green-600',
        ringColor: 'ring-green-300',
        description: '≥ 75%',
        recommendation: 'Provide advanced challenges'
      },
      noData: {
        label: 'No Data',
        icon: Minus,
        color: 'gray',
        bgGradient: 'from-gray-50 to-gray-100',
        borderColor: 'border-gray-200',
        textColor: 'text-gray-700',
        iconBg: 'bg-gray-100',
        iconColor: 'text-gray-600',
        ringColor: 'ring-gray-300',
        description: 'Missing',
        recommendation: 'Data entry required'
      }
    };
    return configs[category] || configs.noData;
  };

  const getSubjectPerformance = (subjects) => {
    return Object.keys(subjects).map(code => {
      const lowerCode = code.toLowerCase();
      const subjectInfo = SUBJECT_MAP[lowerCode];
      const marks = Number(subjects[code]) || 0;
      const maxMarks = subjectInfo?.max || 20;
      const percent = (marks / maxMarks) * 100;
      
      return {
        code: subjectInfo?.code || code.toUpperCase(),
        name: subjectInfo?.name || code,
        marks,
        maxMarks,
        percent: percent.toFixed(1)
      };
    });
  };

  const renderCategoryCard = (category, count) => {
    const config = getCategoryConfig(category);
    const Icon = config.icon;
    const isActive = activeCategory === category;
    const percentage = totalStudents > 0 ? ((count / totalStudents) * 100).toFixed(1) : 0;

    return (
      <button
        key={category}
        onClick={() => setActiveCategory(category)}
        className={`relative p-4 rounded-xl border-2 transition-all hover:shadow-lg ${
          isActive ? `${config.ringColor} ring-2 shadow-md` : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className={`absolute inset-0 bg-gradient-to-br ${config.bgGradient} rounded-xl opacity-50`} />
        <div className="relative">
          <div className="flex items-center justify-between mb-2">
            <div className={`p-2 rounded-lg ${config.iconBg}`}>
              <Icon className={`w-5 h-5 ${config.iconColor}`} />
            </div>
            <div className={`text-xs font-medium ${config.textColor}`}>
              {percentage}%
            </div>
          </div>
          <div className="text-left">
            <div className={`text-2xl font-bold ${config.textColor} mb-1`}>{count}</div>
            <div className="text-sm font-semibold text-gray-700">{config.label}</div>
            <div className="text-xs text-gray-500 mt-1">{config.description}</div>
          </div>
        </div>
      </button>
    );
  };

  const renderStudentRow = (student) => {
    const p = student.percentage;
    const isExpanded = expandedStudent === student.id;
    const subjectPerf = getSubjectPerformance(student.subjects);
    
    let category = 'noData';
    if (Number.isFinite(p)) {
      if (p < 40) category = 'needsSupport';
      else if (p < 60) category = 'developing';
      else if (p < 75) category = 'proficient';
      else category = 'advanced';
    }
    
    const config = getCategoryConfig(category);

    return (
      <React.Fragment key={student.id}>
        <tr 
          className="hover:bg-gray-50 cursor-pointer transition-colors"
          onClick={() => setExpandedStudent(isExpanded ? null : student.id)}
        >
          <td className="px-4 py-3 text-sm font-medium text-gray-700">
            {student.rollNumber || '—'}
          </td>
          <td className="px-4 py-3">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${config.iconBg}`} />
              <div>
                <div className="text-sm font-medium text-gray-800">
                  {student.studentName || '—'}
                </div>
                <div className="text-xs text-gray-500">
                  {student.division} {student.batch && `• ${student.batch}`}
                </div>
              </div>
            </div>
          </td>
          <td className="px-4 py-3 text-center">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${config.iconBg} ${config.textColor}`}>
              {config.label}
            </span>
          </td>
          <td className="px-4 py-3 text-right">
            <div className={`text-sm font-bold ${config.textColor}`}>
              {Number.isFinite(p) ? `${p.toFixed(2)}%` : 'N/A'}
            </div>
            {Number.isFinite(p) && (
              <div className="text-xs text-gray-500">
                {student.total || 0}/105
              </div>
            )}
          </td>
          <td className="px-4 py-3 text-center">
            <ChevronRight 
              className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            />
          </td>
        </tr>
        
        {isExpanded && (
          <tr>
            <td colSpan="5" className="px-4 py-3 bg-gradient-to-r from-gray-50 to-white">
              <div className="p-4 border-l-4 border-indigo-400">
                <div className="mb-3">
                  <h5 className="text-sm font-semibold text-gray-700 mb-2">Subject-wise Performance</h5>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {subjectPerf.length > 0 ? subjectPerf.map((sub) => (
                      <div key={sub.code} className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="text-xs font-semibold text-gray-600 mb-1">{sub.code}</div>
                        <div className="flex items-baseline gap-2">
                          <span className={`text-lg font-bold ${
                            Number(sub.percent) >= 75 ? 'text-green-600' :
                            Number(sub.percent) >= 60 ? 'text-blue-600' :
                            Number(sub.percent) >= 40 ? 'text-orange-600' :
                            'text-red-600'
                          }`}>
                            {sub.marks}
                          </span>
                          <span className="text-xs text-gray-500">/{sub.maxMarks}</span>
                          <span className="ml-auto text-xs font-medium text-gray-600">
                            {sub.percent}%
                          </span>
                        </div>
                        <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                          <div 
                            className={`h-1.5 rounded-full ${
                              Number(sub.percent) >= 75 ? 'bg-green-500' :
                              Number(sub.percent) >= 60 ? 'bg-blue-500' :
                              Number(sub.percent) >= 40 ? 'bg-orange-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${sub.percent}%` }}
                          />
                        </div>
                      </div>
                    )) : (
                      <div className="col-span-3 text-center text-sm text-gray-400 py-2">
                        No subject data available
                      </div>
                    )}
                  </div>
                </div>
                
                <div className={`mt-3 p-3 rounded-lg ${config.bgGradient} border ${config.borderColor}`}>
                  <div className="text-xs font-semibold text-gray-700 mb-1">Recommendation:</div>
                  <div className={`text-sm ${config.textColor}`}>{config.recommendation}</div>
                </div>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg p-6 w-full">
      <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-md">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Performance Analysis
            </h3>
            <p className="text-sm text-gray-500">
              Student categorization by achievement levels
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={selectedTest}
            onChange={(e) => setSelectedTest(e.target.value)}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
          >
            <option value="iaTest1">IAT-1</option>
            <option value="iaTest2">IAT-2</option>
          </select>
        </div>
      </div>

      {/* Statistics Banner */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-gray-600 mb-1">Total Students</div>
            <div className="text-2xl font-bold text-gray-800">{totalStudents}</div>
          </div>
          <div>
            <div className="text-xs text-gray-600 mb-1">Class Average</div>
            <div className="text-2xl font-bold text-indigo-600">{avgPercentage}%</div>
          </div>
          <div>
            <div className="text-xs text-gray-600 mb-1">Pass Rate (≥40%)</div>
            <div className="text-2xl font-bold text-green-600">
              {totalStudents > 0 ? (((totalStudents - categorized.needsSupport.length) / totalStudents) * 100).toFixed(1) : 0}%
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-600 mb-1">Excellence (≥75%)</div>
            <div className="text-2xl font-bold text-purple-600">
              {totalStudents > 0 ? ((categorized.advanced.length / totalStudents) * 100).toFixed(1) : 0}%
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center py-8">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mx-auto" />
            <p className="mt-3 text-sm text-gray-500">Loading performance data...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 text-red-600 bg-red-50 p-4 rounded-lg border border-red-200">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      ) : (
        <>
          {/* Category Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <button
              onClick={() => setActiveCategory('all')}
              className={`p-4 rounded-xl border-2 transition-all hover:shadow-lg ${
                activeCategory === 'all' ? 'ring-2 ring-indigo-300 border-indigo-300 shadow-md bg-gradient-to-br from-indigo-50 to-purple-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-left">
                <div className="text-2xl font-bold text-indigo-600 mb-1">{totalStudents}</div>
                <div className="text-sm font-semibold text-gray-700">All Students</div>
                <div className="text-xs text-gray-500 mt-1">View everyone</div>
              </div>
            </button>
            
            {renderCategoryCard('needsSupport', categorized.needsSupport.length)}
            {renderCategoryCard('developing', categorized.developing.length)}
            {renderCategoryCard('proficient', categorized.proficient.length)}
            {renderCategoryCard('advanced', categorized.advanced.length)}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by roll number or name..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 text-sm"
              />
            </div>
            <select
              value={divisionFilter}
              onChange={(e) => setDivisionFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
            >
              <option value="All">All Divisions</option>
              {divisions.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          {/* Students Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Roll No.
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {activeList.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-sm text-gray-500">
                        No students match the current filters.
                      </td>
                    </tr>
                  ) : (
                    activeList.map(renderStudentRow)
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div>
              Showing <span className="font-semibold text-gray-700">{activeList.length}</span> of{' '}
              <span className="font-semibold text-gray-700">{totalStudents}</span> students
            </div>
            <div className="text-xs text-gray-400">
              Click on any student row to view detailed subject performance
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default SlowAverageFastLearnersWidget;